import { CaretRight } from "@phosphor-icons/react";
import clsx from "clsx";
import { useMemo, useState } from "react";

import MemberPermissionsTag from "common/components/MemberPermissionsTag";
import MobileListTable from "common/components/MobileListTable";
import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import { Button } from "common/components/ui/Button";
import ProfileImage from "common/components/ui/ProfileImage";
import type { Member } from "common/types";

import MobileMemberDetails from "./MobileMemberDetails";

import type { ColumnDef } from "@tanstack/react-table";

export default function MobileList({ members }: { members: Member[] }) {
  const [actionState, setActionState] = useState<{
    isOpen: boolean;
    selectedMember: Member;
  }>({ isOpen: false, selectedMember: null });

  const { isOpen, selectedMember } = actionState;

  const columns: ColumnDef<Member>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Member's name",
        cell: ({
          row: {
            original: {
              first_name: firstName,
              last_name: lastName,
              role,
              email,
              status,
            },
          },
        }) => (
          <div className="flex flex-1 flex-row items-center gap-2">
            {status === "pending" ? (
              <ProfileImage src="" />
            ) : (
              <AimfoxAvatar firstName={firstName} lastName={lastName} />
            )}

            <div className="flex flex-col justify-center gap-0.5">
              <span
                className={clsx(
                  "line-clamp-1 break-all text-start text-sm-bold",
                  status === "pending" && "text-black-400",
                )}
              >
                {status === "pending"
                  ? `New ${role === "member" ? "Member" : "Admin"}`
                  : `${firstName} ${lastName}`}
              </span>

              <span
                className={clsx(
                  "line-clamp-1 break-all text-start text-xs-medium text-black-400",
                  status === "pending" ? "text-black-400" : "text-black-500",
                )}
              >
                {email}
              </span>
            </div>
          </div>
        ),
      },
      {
        id: "role",
        header: "Role",
        cell: ({
          row: {
            original: { permissions, role },
          },
        }) => <MemberPermissionsTag role={role} permissions={permissions} />,
      },
      {
        id: "actions",
        cell: () => (
          <Button intent="labelIcon" size="sm" variant="ghost-black">
            <CaretRight />
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <MobileListTable
        columns={columns}
        data={members}
        isLoading={false}
        onRowClick={(member) =>
          setActionState({ isOpen: true, selectedMember: member })
        }
      />
      {selectedMember && (
        <MobileMemberDetails
          member={selectedMember}
          isOpen={isOpen}
          setIsOpen={(isOpen) => setActionState({ ...actionState, isOpen })}
        />
      )}
    </>
  );
}
